import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import VideoAnnotation from "core/components/VideoAnnotation";
import moment from "moment";

function StreamAnnotationsOverlay({ timecodes, rawProgramDateTime, paused }) {
  const [activeAnnotations, setActiveAnnotations] = useState([]);
  const manuallyClosedIds = useRef(new Set());

  useEffect(() => {
    // Фильтруем только аннотации с absoluteTime
    const annotations =
      timecodes?.filter((tc) => tc.type === "ANNOTATION" && tc.absoluteTime) ||
      [];

    if (!annotations.length) {
      setActiveAnnotations([]);
      return;
    }

    // При паузе не обновляем список (оставляем текущие аннотации)
    if (paused) {
      return;
    }

    const updateActiveAnnotations = () => {
      // Вычисляем текущее абсолютное время
      // Если есть rawProgramDateTime из плеера - используем его
      // Иначе используем текущее время браузера
      const currentAbsoluteTime = rawProgramDateTime
        ? moment(rawProgramDateTime)
        : moment();

      const activeList = [];

      annotations.forEach((annotation) => {
        const startTime = moment(annotation.absoluteTime);
        const endTime = moment(annotation.absoluteTime).add(
          annotation.duration || 5,
          "seconds"
        );

        // Фильтруем аннотации где currentAbsoluteTime >= annotation.absoluteTime
        // Показываем аннотацию если currentAbsoluteTime < annotation.absoluteTime + annotation.duration
        if (
          currentAbsoluteTime.isSameOrAfter(startTime) &&
          currentAbsoluteTime.isBefore(endTime)
        ) {
          // И не закрыта вручную
          if (!manuallyClosedIds.current.has(annotation.id)) {
            activeList.push(annotation);
          }
        }
      });

      setActiveAnnotations(activeList);
    };

    // Обновляем сразу
    updateActiveAnnotations();

    // Если нет rawProgramDateTime, обновляем каждую секунду для проверки аннотаций
    // (так как время браузера меняется)
    if (!rawProgramDateTime) {
      const interval = setInterval(updateActiveAnnotations, 1000);
      return () => clearInterval(interval);
    }
  }, [timecodes, rawProgramDateTime, paused]);

  const handleClose = (annotation) => {
    // Запоминаем что пользователь закрыл эту аннотацию
    manuallyClosedIds.current.add(annotation.id);

    // Удаляем из активных только конкретную аннотацию
    setActiveAnnotations((prev) => prev.filter((a) => a.id !== annotation.id));
  };

  if (!activeAnnotations.length) {
    return null;
  }

  // Группируем аннотации по позиции для правильного стекирования
  const annotationsByPosition = activeAnnotations.reduce((acc, annotation) => {
    const position = annotation.position || "TOP_LEFT";
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(annotation);
    return acc;
  }, {});

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 200,
      }}
    >
      {Object.entries(annotationsByPosition).map(([position, annotations]) => (
        <React.Fragment key={position}>
          {annotations.map((annotation, index) => (
            <VideoAnnotation
              key={annotation.id}
              annotation={annotation}
              onClose={() => handleClose(annotation)}
              index={index}
            />
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
}

StreamAnnotationsOverlay.propTypes = {
  timecodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string,
      time: PropTypes.number,
      absoluteTime: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      icon: PropTypes.string,
      duration: PropTypes.number,
      position: PropTypes.string,
      linkedContent: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        siteUrl: PropTypes.string,
        thumbnails: PropTypes.arrayOf(
          PropTypes.shape({
            format: PropTypes.string,
            url: PropTypes.string,
            webpUrl: PropTypes.string,
          })
        ),
      }),
    })
  ),
  rawProgramDateTime: PropTypes.string,
  paused: PropTypes.bool,
};

StreamAnnotationsOverlay.defaultProps = {
  timecodes: [],
  paused: false,
};

export default StreamAnnotationsOverlay;
